'use client';

import classNames from 'classnames';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import Image from 'next/image';

import { PromoSource, Routes } from '@/app/enums';
import { PropsWithClassname } from '@/app/types';
import { formatPriceInCents } from '@/app/utils/formatting';
import messageIllustration from '@/public/public/images/message-illustration.png';

import { ActiveGoodHabitVoucherResponse } from '@/app/api/models';
import Button, { ButtonHTMLTag, ButtonStyle } from '@/app/components/Button/Button';
import { ActiveGoodHabitDonationResponse } from '@/src/api/models';
import { instanceOfActiveGoodHabitVoucherResponse } from '@/src/api/utils';

interface GoodHabitBarWidgetProps extends PropsWithClassname {
  activeGoodHabit: ActiveGoodHabitDonationResponse | ActiveGoodHabitVoucherResponse;
}

const GoodHabitBarWidget = ({ activeGoodHabit, className }: GoodHabitBarWidgetProps) => {
  const { t } = useTranslation();

  const disponibleAmountI18nKey = useMemo(() => {
    const goodhabit = activeGoodHabit as object;

    if (instanceOfActiveGoodHabitVoucherResponse(goodhabit)) {
      if (!goodhabit.remainingAmountInCents) {
        return 'components.GoodHabitBarWidget.voucherLastDisponibleAmount';
      }

      return 'components.GoodHabitBarWidget.voucherDisponibleAmount';
    }

    return 'components.GoodHabitBarWidget.donationDisponibleAmount';
  }, [activeGoodHabit]);

  return (
    <div className={classNames(className, 'px-[24px] lg:px-0')}>
      <div
        className={
          'flex items-center bg-white w-full default-page-max-width mx-auto rounded shadow-md border border-neutral-light px-[24px] py-[13px]'
        }
      >
        <Image src={messageIllustration} alt="message illustration" className="w-[48px] h-[48px] mr-[24px]" />
        <div className="grow flex flex-col items-start lg:flex-row lg:items-center lg:justify-between">
          <span>
            <Trans
              i18nKey={disponibleAmountI18nKey}
              values={{
                amount: formatPriceInCents(activeGoodHabit.donation.amountInCents),
                nextMonthAmount: formatPriceInCents(
                  (activeGoodHabit as ActiveGoodHabitVoucherResponse).remainingAmountInCents ?? 0,
                ),
              }}
            >
              Na tento měsíc máte k dispozici&nbsp;<b className="font-medium">amount</b>. Na další měsíce vám
              zbývá&nbsp;
              <b className="font-medium">nextMonthAmount</b>
            </Trans>
          </span>
          <Button
            tagSpecificProps={{
              htmlTag: ButtonHTMLTag.LINK,
              href: `${process.env.NEXT_PUBLIC_REDIRECT_URL}/${Routes.ACTIVE_COLLECTIONS}?promo-source=${PromoSource.SUBSCRIPTION_BAR}`,
            }}
            style={ButtonStyle.PRIMARY_LINK}
          >
            {t('components.GoodHabitBarWidget.pickAProject')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GoodHabitBarWidget;
